/**
 * PDP Tabs — pdp-tabs.js
 *
 * Behaviour:
 *  1. Only the active panel is visible; others are display:none
 *  2. Clicking a tab swaps the visible panel with a fade+slide animation
 *  3. Animated ink underline slides to the active tab
 *  4. Tab nav scrolls horizontally to keep the active tab in view
 *  5. Sticky nav gains a drop shadow when stuck
 *  6. Keyboard arrow navigation (ARIA tabs pattern)
 */

(function () {
  'use strict';

  document.querySelectorAll('.pdp-tabs-section').forEach(function (section) {

    const navWrap = section.querySelector('.pts-nav-wrap');
    const nav     = section.querySelector('.pts-nav');
    const ink     = section.querySelector('.pts-ink');
    const tabs    = Array.from(section.querySelectorAll('.pts-tab'));
    const panels  = Array.from(section.querySelectorAll('.pts-panel'));

    if (!tabs.length || !panels.length) return;

    /* ── sticky-top CSS variable (matches theme header height) ── */
    function stickyTopOffset() {
      const outer = section.closest('.pdp-tabs-outer') || section;
      const raw   = getComputedStyle(outer).getPropertyValue('--pts-sticky-top') || '0px';
      return parseInt(raw, 10) || 0;
    }

    /* ============================================================
       INK UNDERLINE
       ============================================================ */
    function moveInkTo(tab) {
      if (!ink || !nav) return;
      const navRect = nav.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      ink.style.transform = 'translateX(' + (tabRect.left - navRect.left + nav.scrollLeft) + 'px)';
      ink.style.width     = tabRect.width + 'px';
      if (!ink.classList.contains('ready')) ink.classList.add('ready');
    }

    /* ============================================================
       SCROLL TAB BUTTON INTO HORIZONTAL VIEW
       ============================================================ */
    function scrollTabIntoView(tab) {
      if (!nav) return;
      const navRect = nav.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const pad = 16;
      if (tabRect.left < navRect.left + pad) {
        nav.scrollLeft -= (navRect.left + pad - tabRect.left);
      } else if (tabRect.right > navRect.right - pad) {
        nav.scrollLeft += (tabRect.right - (navRect.right - pad));
      }
    }

    /* ============================================================
       ACTIVATE TAB — swap panel, move ink, update ARIA
       ============================================================ */
    function activateTab(index) {
      tabs.forEach(function (t, i) {
        const on = i === index;
        t.classList.toggle('active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });

      panels.forEach(function (p, i) {
        const on = i === index;
        p.classList.toggle('active', on);
        p.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      moveInkTo(tabs[index]);
      scrollTabIntoView(tabs[index]);
    }

    /* ============================================================
       TAB CLICKS
       ============================================================ */
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { activateTab(i); });
    });

    /* ============================================================
       KEYBOARD NAVIGATION (ARIA tabs pattern)
       ============================================================ */
    tabs.forEach(function (tab, i) {
      tab.addEventListener('keydown', function (e) {
        let next = i;
        if      (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft')  next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home')       next = 0;
        else if (e.key === 'End')        next = tabs.length - 1;
        else return;

        e.preventDefault();
        activateTab(next);
        tabs[next].focus();
      });
    });

    /* ============================================================
       STICKY SHADOW — shadow appears when nav bar is stuck
       ============================================================ */
    if (navWrap) {
      /* Insert a 1px sentinel just above the nav wrap.
         When it leaves the viewport, the nav is stuck. */
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'height:1px;margin-bottom:-1px;pointer-events:none;';
      navWrap.parentNode.insertBefore(sentinel, navWrap);

      new IntersectionObserver(function (entries) {
        navWrap.classList.toggle('is-stuck', !entries[0].isIntersecting);
      }, { threshold: [1] }).observe(sentinel);
    }

    /* ============================================================
       INIT
       ============================================================ */
    requestAnimationFrame(function () {
      /* Position ink under whichever tab starts active */
      const activeTab = tabs.find(function (t) { return t.classList.contains('active'); }) || tabs[0];
      if (activeTab) moveInkTo(activeTab);
    });

    /* Reposition ink on resize / orientation change */
    let resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        const activeTab = tabs.find(function (t) { return t.classList.contains('active'); });
        if (activeTab) moveInkTo(activeTab);
      }, 120);
    }, { passive: true });

  }); /* end forEach section */

})();
